import { TenantTokenManager } from './TenantTokenManager';
import { DataField, TelemetryEvent, TelemetrySink, CustomerContentEvent } from './DataModels';
import { TelemetryEventValidator } from './TelemetryEventValidator';
import { logNotification, LogLevel, Category, logError } from './OTelNotifications';
import { oteljsVersion } from './Version';
import { makeStringDataField, makeInt64DataField } from './DataFieldHelper';

const CUSTOMER_CONTENT_VERSION = 1;

/**
 * Clone a telemetry event; generally so we don't modify the input telemetry event
 * Also initializes telemetryProperties and dataFields if they aren't already set
 * @param telemetryEvent - The event to clone
 */
export function cloneEvent(telemetryEvent: TelemetryEvent): TelemetryEvent {
  let localEvent: TelemetryEvent = { eventName: telemetryEvent.eventName, eventFlags: telemetryEvent.eventFlags };

  localEvent.telemetryProperties = !!telemetryEvent.telemetryProperties ? { ...telemetryEvent.telemetryProperties } : {};

  if (!!telemetryEvent.eventContract) {
    localEvent.eventContract = { name: telemetryEvent.eventContract.name, dataFields: telemetryEvent.eventContract.dataFields.slice() };
  }

  localEvent.dataFields = !!telemetryEvent.dataFields ? telemetryEvent.dataFields.slice() : [];
  localEvent.timestamp = telemetryEvent.timestamp;
  return localEvent;
}

export interface ISimpleTelemetryLogger {
  /**
   * A list of sinks that telemetry events will be forwarded to
   */
  telemetrySinks: TelemetrySink[];

  /**
   * persistentDataFields: Data fields that will automatically be sent with every event
   */
  persistentDataFields: DataField[];

  /**
   * Send a telemetry event
   * @param telemetryEvent - The event to send
   */
  sendTelemetryEvent(telemetryEvent: TelemetryEvent): void;

  /**
   * Sends an event containing customer content, to a compliant endpoint
   * @param telemetryEvent - The event to send
   * @param customerContentType - The type of customer content
   */
  sendCustomerContent(customerContentEvent: CustomerContentEvent): void;

  /**
   * Add a sink for processing telemetry events
   * @param sink - A telemetry sink to which events will be passed when sendTelemetryEvent is called.
   */
  addSink(sink: TelemetrySink): void;

  /**
   * Set tokens to use for sending telemetry to Aria and/or Nexus
   * The tokens will be used for events which whose namespace exactly matches
   * @param namespace - The namespace for the tokens
   * @param ariaTenantToken - Optional: The Aria tenant token
   * @param nexusTenantToken - Optional: The Nexus tenant token
   */
  setTenantToken(namespace: string, ariaTenantToken?: string, nexusTenantToken?: number): void;

  /**
   * Get the currently applied telemetry configuration
   */
  getConfig(): ITelemetryConfig;
}

/**
 * A configuration object for telemetry sinks
 */
export interface ITelemetryConfig {
  /**
   * NOT FOR USE IN BYPASSING VALIDATION RULES; ONLY FOR USE IN IMPROVING PERFORMANCE
   *
   * Disables basic validation that telemetry events conform to Office Event Schema
   * Validation adds a small overhead that could adversely affect perf in production.
   * In pre-production, validation should be turned on to ensure that events are valid.
   * To improve perf, validation can be turned off in production. This flag MUST NOT
   * be used to bypass rules that would otherwise prevent events from being sent.
   */
  disableValidation?: boolean;
}

/**
 * SimpleTelemetryLogger is the base implementation of a telemetry logger.
 * It exposes sendTelemetryEvent, which is used to log a telemetry event.
 */
export class SimpleTelemetryLogger implements ISimpleTelemetryLogger {
  /**
   * A list of sinks that telemetry events will be forwarded to
   */
  telemetrySinks: TelemetrySink[] = [];

  /**
   * @internal Internal variable; do not use publicly.
   */
  persistentDataFields: DataField[] = [];

  private config: ITelemetryConfig;

  /**
   * Creates a telemetry logger
   * @param parent - A parent telemetry logger that this telemetry logger should inherit from
   *                 This telemetry logger will send telemetry to the same sinks
   *                 and will send the same persistent data fields as the parent logger
   * @param persistentDataFields - Data fields to send with every event
   */
  constructor(parent?: ISimpleTelemetryLogger, persistentDataFields?: DataField[], config?: ITelemetryConfig) {
    this.config = config || {};
    if (parent) {
      this.telemetrySinks = parent.telemetrySinks;
      this.persistentDataFields.push(...parent.persistentDataFields);
      this.config = { ...parent.getConfig(), ...this.config };
    } else {
      this.persistentDataFields.push(makeStringDataField('OTelJS.Version', oteljsVersion));
    }
    if (persistentDataFields) {
      this.persistentDataFields.push(...persistentDataFields);
    }
  }

  /**
   * SendTelemetryEvent is the basic way to send a telemetry event.
   * This should be used for sending telemetry about discrete events that happened
   * For telemetry that includes duration and/or success/failure, the Activity API may be more appropriate.
   * @param telemetryEvent - The telemetry event to send
   */
  public sendTelemetryEvent(telemetryEvent: TelemetryEvent) {
    if (
      telemetryEvent.telemetryProperties &&
      (telemetryEvent.telemetryProperties.customerContentVersion || telemetryEvent.telemetryProperties.customerContentType)
    ) {
      logNotification(LogLevel.Error, Category.Core, () => 'Customer content');
      return;
    }
    const clonedEvent = cloneEvent(telemetryEvent);
    // If either one is missing, we try to fill both
    if (!clonedEvent.telemetryProperties!.ariaTenantToken || !clonedEvent.telemetryProperties!.nexusTenantToken) {
      const telemetryProperties = TenantTokenManager.getTenantTokens(telemetryEvent.eventName);
      if (telemetryProperties) {
        clonedEvent.telemetryProperties!.ariaTenantToken = telemetryProperties.ariaTenantToken;
        clonedEvent.telemetryProperties!.nexusTenantToken = telemetryProperties.nexusTenantToken;
      } else {
        logNotification(LogLevel.Error, Category.Core, () => 'No tenant token: ' + telemetryEvent.eventName);
        return;
      }
    }
    this.sendTelemetryEventInternal(clonedEvent);
  }

  /**
   * Sends customer content to an appropriate compliant endpoint
   * @param event - The event to send
   * @param customerContentType - The type of customer content
   */
  public sendCustomerContent(customerContentEvent: CustomerContentEvent) {
    const clonedEvent = cloneEvent(customerContentEvent);
    // cloneEvent initialized all the properties if they were not already set
    if (
      !clonedEvent.telemetryProperties!.customerContentVersion ||
      clonedEvent.telemetryProperties!.customerContentVersion > CUSTOMER_CONTENT_VERSION
    ) {
      clonedEvent.telemetryProperties!.customerContentVersion = CUSTOMER_CONTENT_VERSION;
    }
    // Suppress any tenant tokens that were passed in; the event must go to the customer content tenant.
    clonedEvent.telemetryProperties!.ariaTenantToken = undefined;
    clonedEvent.telemetryProperties!.nexusTenantToken = undefined;

    // This is the EventContent data contract. Hardcoding it like this to save bytes.
    // EventContent.Type.CustomerContent is 2.
    clonedEvent.dataFields!.push(makeInt64DataField('EventContent.Type', 2));
    return this.sendTelemetryEventInternal(clonedEvent);
  }

  /**
   * Internal method to send a telemetry event
   * @param event - The telemetry event to send.
   * Note that it may be a CustomerContentEvent by virtue of having a customerContentVersion and customerContentType.
   */
  protected sendTelemetryEventInternal(telemetryEvent: TelemetryEvent) {
    try {
      if (this.telemetrySinks.length === 0) {
        logNotification(LogLevel.Warning, Category.Core, () => 'No telemetry sinks are attached.');
        return;
      }

      this.processTelemetryEvent(telemetryEvent);
    } catch (error) {
      logError(Category.Core, 'SendTelemetryEvent', error);
      return;
    }

    try {
      this.telemetrySinks.forEach(telemetrySink => {
        if (
          telemetryEvent.telemetryProperties &&
          telemetryEvent.telemetryProperties.customerContentVersion &&
          telemetryEvent.telemetryProperties.customerContentType
        ) {
          // Skip this sink if it doesn't support customercontent
          if (telemetrySink.sendCustomerContent) {
            telemetrySink.sendCustomerContent(telemetryEvent as CustomerContentEvent);
          }
        } else {
          telemetrySink.sendTelemetryEvent(telemetryEvent);
        }
      });
    } catch (_e) {
      // Sinks should handle their own errors;
    }
  }

  /**
   * Processes a telemetry event, but does not send it
   *   Adds persistent data fields
   *   Validates the event
   * Throws if the event is invalid
   * @param telemetryEvent - The telemetry event to process
   */
  private processTelemetryEvent(telemetryEvent: TelemetryEvent) {
    if (telemetryEvent.dataFields && this.persistentDataFields) {
      // The last-added data field wins, if the same-named data field is added more than once.
      // Persistent data fields should have lower priority than per-event data fields
      telemetryEvent.dataFields.unshift(...this.persistentDataFields);
    }

    if (!this.config.disableValidation) {
      TelemetryEventValidator.validateTelemetryEvent(telemetryEvent);
    }
  }

  /**
   * Adds a telemetry sink, to which events will be sent
   * @param sink - The telemetry sink to add
   */
  public addSink(sink: TelemetrySink) {
    this.telemetrySinks.push(sink);
  }

  /**
   * Sets a tenant token for a single namespace. Events within the namespace will be sent to the corresponding tenant.
   * @param namespace - The namespace to which the tokens apply, such as Office.System.
   * @param ariaTenantToken - The Aria tenant token
   * @param nexusTenantToken - The Nexus tenant token
   */
  public setTenantToken(namespace: string, ariaTenantToken?: string, nexusTenantToken?: number) {
    TenantTokenManager.setTenantTokens(namespace, { ariaTenantToken, nexusTenantToken });
  }

  /**
   * Clone a telemetry event; generally so we don't modify the input telemetry event
   * Also sets dataFields to [] if it isn't already set
   * @param telemetryEvent - The event to clone
   */
  public cloneEvent(telemetryEvent: TelemetryEvent): TelemetryEvent {
    return cloneEvent(telemetryEvent);
  }

  /**
   * Gets the currently applied configuration
   */
  public getConfig() {
    return this.config;
  }
}
